/* eslint-disable no-comments/disallowComments */
import {
  VehicleParamData,
  VehicleParamsDto,
  VehicleParamsFormatted,
  VehicleParamsGroupEnum as VPG,
  VehicleParamsNameEnum as VPN,
} from '@fleet-beat/api';

const tyreTempConfig: VehicleParamData = {
  max: 95, // °C
  normal: 60,
  warning: 75,
};
const tyrePressureConfig: VehicleParamData = {
  max: 900, // kPa (~130 psi)
  dangerLow: 480,
  warningLow: 600,
  normal: 760,
  warning: 830,
};
const voltage12Config: VehicleParamData = {
  max: 14.8,
  min: 11.8,
  dangerLow: 12,
  warningLow: 12.5,
};
const voltage24Config: VehicleParamData = {
  max: 29.6,
  min: 23.6,
  dangerLow: 24,
  warningLow: 25,
};

function getLatestIsoDate(dates: (string | null)[]): string | null {
  const validDates = dates
    .filter((d: string | null): d is string => !!d)
    .map((d: string) => new Date(d).getTime())
    .filter((t: number) => !isNaN(t));

  if (!validDates.length) return null;

  const maxTime = Math.max(...validDates);
  return new Date(maxTime).toISOString();
}

export function formatParams(params: VehicleParamsDto[]): VehicleParamsFormatted | undefined {
  if (!params || !params.length) return undefined;
  const pMap = Object.fromEntries(params.map((i: VehicleParamsDto) => [`${i.group}.${i.name}`, i]));
  const get = (key: string): number => (pMap?.[key] ? Number(pMap[key]?.value || 0) : null);
  const getLastSync = (key: string): string => (pMap?.[key] ? pMap[key]?.updatedAt || null : null);
  const cToF = (c: number | null): number | null => (c != null ? +(c * (9 / 5) + 32).toFixed() : null);
  const kpaToPsi = (kpa: number | null): number | null => (kpa != null ? +(kpa * 0.145038).toFixed() : null);
  const kpaToBar = (kpa: number | null): number | null => (kpa != null ? +(kpa / 100).toFixed(1) : null);

  // NOTE | FUEL Level
  const fuelLevelValue: number | null = get(`${VPG.ENVIRONMENT}.${VPN.FUEL_LEVEL}`);
  const fuelLevelLSTime: string | null = getLastSync(`${VPG.ENVIRONMENT}.${VPN.FUEL_LEVEL}`);

  // NOTE | FUEL Rate
  const fuelRateInGal: number | null = get(`${VPG.ECU}.${VPN.FUEL_CONSUMPTION}`);
  const fuelRateInGalValue: number | null = fuelRateInGal !== null ? +(fuelRateInGal * 0.264172).toFixed(1) : null; // NOTE to gal/hr

  // NOTE | SPEED
  const speed: number | null = get(`${VPG.ENVIRONMENT}.${VPN.SPEED}`);
  const speedValue: number | null = speed !== null ? +(speed * 0.621371).toFixed() : null;
  const speedLSTime: string | null = getLastSync(`${VPG.ENVIRONMENT}.${VPN.SPEED}`);

  // NOTE | ODOMETER
  const odometer: number | null = get(`${VPG.ENVIRONMENT}.${VPN.ODOMETER}`);
  const odometerValue: number | null = odometer !== null ? +(odometer * 0.621371).toFixed() : null;
  const odometerLSTime: string | null = getLastSync(`${VPG.ENVIRONMENT}.${VPN.ODOMETER}`);

  // NOTE | ENGINE Hours
  const engineHours: number | null = get(`${VPG.ECU}.${VPN.ENGINE_HOURS}`);
  const engineHoursValue: number | null = engineHours !== null ? +engineHours.toFixed(1) : null;
  const engineHoursLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.ENGINE_HOURS}`);

  // NOTE | RPM
  const rpmValue: number | null = get(`${VPG.ECU}.${VPN.ENGINE_RPM}`);
  const rpmLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.ENGINE_RPM}`);

  // NOTE | ENGINE Temp
  const engineTemp: number | null = get(`${VPG.ECU}.${VPN.ENGINE_TEMP}`);
  const engineTempValue: number | null = cToF(engineTemp);
  const engineTempLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.ENGINE_TEMP}`);

  // NOTE | OIL Temp
  const oilTemp: number | null = get(`${VPG.ECU}.${VPN.ENGINE_OIL_TEMP}`);
  const oilTempValue: number | null = cToF(oilTemp);
  const oilTempLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.ENGINE_OIL_TEMP}`);

  // NOTE | OIL Pressure
  const oilPress: number | null = get(`${VPG.ECU}.${VPN.ENGINE_OIL_PRESS}`);
  const oilPressValue: number | null = kpaToPsi(oilPress); // NOTE kPa to psi
  const oilPressLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.ENGINE_OIL_PRESS}`);

  // NOTE | INTAKE Temp
  const intakeTemp: number | null = get(`${VPG.ECU}.${VPN.INTAKE_AIR_TEMP}`);
  const intakeTempValue: number | null = cToF(intakeTemp);
  const intakeTempLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.INTAKE_AIR_TEMP}`);

  // NOTE | INLET Temp
  const inletTemp: number | null = get(`${VPG.ECU}.${VPN.INLET_AIR_TEMP}`);
  const inletTempValue: number | null = cToF(inletTemp);
  const inletTempLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.INLET_AIR_TEMP}`);

  // NOTE | ENGINE Load
  const engineLoadValue: number | null = get(`${VPG.ECU}.${VPN.ENGINE_LOAD}`);
  const engineLoadLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.ENGINE_LOAD}`);

  // NOTE | ENGINE Throttle Pose
  const engineThrottlePoseValue: number | null = get(`${VPG.ECU}.${VPN.ENGINE_THROTTLE_POSE}`);
  const engineThrottlePoseLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.ENGINE_THROTTLE_POSE}`);

  // NOTE | Mass air flow sensor
  const maf: number | null = get(`${VPG.ECU}.${VPN.MASS_AIR_FLOW_SENSOR}`);
  const mafValue: number | null = maf !== null ? +(maf * 0.132277).toFixed() : null; // NOTE to lb/min
  const mafLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.MASS_AIR_FLOW_SENSOR}`);

  // NOTE | Manifold absolute pressure
  const map: number | null = get(`${VPG.ECU}.${VPN.MANIFOLD_ABSOLUTE_PRESSURE}`);
  const mapValue: number | null = kpaToPsi(map); // NOTE kPa to psi
  const mapLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.MANIFOLD_ABSOLUTE_PRESSURE}`);

  // NOTE | Tires temp and pressure
  // NOTE | Tires FRONT LEFT
  const tyreFrontLeftTemp: number | null = get(`${VPG.ECU}.${VPN.TYRE_FRONT_LEFT_TEMP}`);
  const tyreFrontLeftTempValue: number | null = cToF(tyreFrontLeftTemp);

  const tyreFrontLeftPressure: number | null = get(`${VPG.ECU}.${VPN.TYRE_FRONT_LEFT_PRESSURE}`);
  const tyreFrontLeftPressurePsi: number | null = kpaToPsi(tyreFrontLeftPressure);
  const tyreFrontLeftPressureBar: number | null = kpaToBar(tyreFrontLeftPressure);

  // NOTE | Tires FRONT RIGHT
  const tyreFrontRightTemp: number | null = get(`${VPG.ECU}.${VPN.TYRE_FRONT_RIGHT_TEMP}`);
  const tyreFrontRightTempValue: number | null = cToF(tyreFrontRightTemp);

  const tyreFrontRightPressure: number | null = get(`${VPG.ECU}.${VPN.TYRE_FRONT_RIGHT_PRESSURE}`);
  const tyreFrontRightPressurePsi: number | null = kpaToPsi(tyreFrontRightPressure);
  const tyreFrontRightPressureBar: number | null = kpaToBar(tyreFrontRightPressure);

  // NOTE | Tires LEFT OUTSIDE
  const tyreRearLeftOutsideTemp: number | null = get(`${VPG.ECU}.${VPN.TYRE_REAR_LEFT_OUTSIDE_TEMP}`);
  const tyreRearLeftOutsideTempValue: number | null = cToF(tyreRearLeftOutsideTemp);

  const tyreRearLeftOutsidePressure: number | null = get(`${VPG.ECU}.${VPN.TYRE_REAR_LEFT_OUTSIDE_PRESSURE}`);
  const tyreRearLeftOutsidePressurePsi: number | null = kpaToPsi(tyreRearLeftOutsidePressure);
  const tyreRearLeftOutsidePressureBar: number | null = kpaToBar(tyreRearLeftOutsidePressure);

  // NOTE | Tires RIGHT OUTSIDE
  const tyreRearRightOutsideTemp: number | null = get(`${VPG.ECU}.${VPN.TYRE_REAR_RIGHT_OUTSIDE_TEMP}`);
  const tyreRearRightOutsideTempValue: number | null = cToF(tyreRearRightOutsideTemp);

  const tyreRearRightOutsidePressure: number | null = get(`${VPG.ECU}.${VPN.TYRE_REAR_RIGHT_OUTSIDE_PRESSURE}`);
  const tyreRearRightOutsidePressurePsi: number | null = kpaToPsi(tyreRearRightOutsidePressure);
  const tyreRearRightOutsidePressureBar: number | null = kpaToBar(tyreRearRightOutsidePressure);

  // NOTE | Tires LEFT INSIDE
  const tyreRearLeftInsideTemp: number | null = get(`${VPG.ECU}.${VPN.TYRE_REAR_LEFT_INSIDE_TEMP}`);
  const tyreRearLeftInsideTempValue: number | null = cToF(tyreRearLeftInsideTemp);

  const tyreRearLeftInsidePressure: number | null = get(`${VPG.ECU}.${VPN.TYRE_REAR_LEFT_INSIDE_PRESSURE}`);
  const tyreRearLeftInsidePressurePsi: number | null = kpaToPsi(tyreRearLeftInsidePressure);
  const tyreRearLeftInsidePressureBar: number | null = kpaToBar(tyreRearLeftInsidePressure);

  // NOTE | Tires RIGHT INSIDE
  const tyreRearRightInsideTemp: number | null = get(`${VPG.ECU}.${VPN.TYRE_REAR_RIGHT_INSIDE_TEMP}`);
  const tyreRearRightInsideTempValue: number | null = cToF(tyreRearRightInsideTemp);

  const tyreRearRightInsidePressure: number | null = get(`${VPG.ECU}.${VPN.TYRE_REAR_RIGHT_INSIDE_PRESSURE}`);
  const tyreRearRightInsidePressurePsi: number | null = kpaToPsi(tyreRearRightInsidePressure);
  const tyreRearRightInsidePressureBar: number | null = kpaToBar(tyreRearRightInsidePressure);

  const tyreFrontLeftTempLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.TYRE_FRONT_LEFT_TEMP}`);
  const tyreFrontLeftPressureLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.TYRE_FRONT_LEFT_PRESSURE}`);
  const tyreFrontRightTempLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.TYRE_FRONT_RIGHT_TEMP}`);
  const tyreFrontRightPressureLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.TYRE_FRONT_RIGHT_PRESSURE}`);
  const tyreRearLeftOutsideTempLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.TYRE_REAR_LEFT_OUTSIDE_TEMP}`);
  const tyreRearLeftOutsidePressureLSTime: string | null = getLastSync(
    `${VPG.ECU}.${VPN.TYRE_REAR_LEFT_OUTSIDE_PRESSURE}`,
  );
  const tyreRearRightOutsideTempLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.TYRE_REAR_RIGHT_OUTSIDE_TEMP}`);
  const tyreRearRightOutsidePressureLSTime: string | null = getLastSync(
    `${VPG.ECU}.${VPN.TYRE_REAR_RIGHT_OUTSIDE_PRESSURE}`,
  );
  const tyreRearLeftInsideTempLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.TYRE_REAR_LEFT_INSIDE_TEMP}`);
  const tyreRearLeftInsidePressureLSTime: string | null = getLastSync(
    `${VPG.ECU}.${VPN.TYRE_REAR_LEFT_INSIDE_PRESSURE}`,
  );
  const tyreRearRightInsideTempLSTime: string | null = getLastSync(`${VPG.ECU}.${VPN.TYRE_REAR_RIGHT_INSIDE_TEMP}`);
  const tyreRearRightInsidePressureLSTime: string | null = getLastSync(
    `${VPG.ECU}.${VPN.TYRE_REAR_RIGHT_INSIDE_PRESSURE}`,
  );

  const tiresLSTime: string | null = getLatestIsoDate([
    tyreFrontLeftTempLSTime,
    tyreFrontLeftPressureLSTime,
    tyreFrontRightTempLSTime,
    tyreFrontRightPressureLSTime,
    tyreRearLeftOutsideTempLSTime,
    tyreRearLeftOutsidePressureLSTime,
    tyreRearRightOutsideTempLSTime,
    tyreRearRightOutsidePressureLSTime,
    tyreRearLeftInsideTempLSTime,
    tyreRearLeftInsidePressureLSTime,
    tyreRearRightInsideTempLSTime,
    tyreRearRightInsidePressureLSTime,
  ]);

  const voltageValue: number | null = get(`${VPG.ENVIRONMENT}.${VPN.BATTERY_VOLTAGE}`);
  const voltageLSTime: string | null = getLastSync(`${VPG.ENVIRONMENT}.${VPN.BATTERY_VOLTAGE}`);

  return {
    fuelLevel: {
      value: fuelLevelValue,
      lastSync: fuelLevelLSTime,
    },
    fuelRate: {
      value: fuelRateInGalValue,
      max: 15,
      warning: 10,
      normal: 8,
    },
    speed: {
      value: speedValue,
      max: 80,
      warning: 65,
      normal: 60,
      lastSync: speedLSTime,
    },
    rpm: {
      value: rpmValue,
      max: 3000,
      warning: 2200,
      normal: 2000,
      lastSync: rpmLSTime,
    },
    engineTemp: {
      value: engineTempValue,
      max: 300,
      warning: 250,
      normal: 220,
      lastSync: engineTempLSTime,
    },
    oilTemp: {
      value: oilTempValue,
      normal: 230,
      warning: 260,
      max: 300,
      lastSync: oilTempLSTime,
    },
    oilPress: {
      value: oilPressValue,
      normal: 70,
      warning: 85,
      max: 100,
      dangerLow: 10,
      warningLow: 20,
      lastSync: oilPressLSTime,
    },
    intakeTemp: {
      value: intakeTempValue,
      max: 200,
      warning: 140,
      normal: 120,
      lastSync: intakeTempLSTime,
    },
    inletTemp: {
      value: inletTempValue,
      max: 200,
      warning: 140,
      normal: 120,
      lastSync: inletTempLSTime,
    },
    engineLoad: {
      value: engineLoadValue,
      max: 100,
      warning: 80,
      normal: 70,
      lastSync: engineLoadLSTime,
    },
    throttle: {
      value: engineThrottlePoseValue,
      max: 100,
      warning: 80,
      normal: 70,
      lastSync: engineThrottlePoseLSTime,
    },
    maf: {
      value: mafValue,
      max: 100,
      warning: 70,
      normal: 60,
      lastSync: mafLSTime,
    },
    map: {
      value: mapValue,
      max: 50,
      warning: 40,
      normal: 30,
      lastSync: mapLSTime,
    },
    odometer: {
      value: odometerValue,
      lastSync: odometerLSTime,
    },
    engineHours: {
      value: engineHoursValue,
      lastSync: engineHoursLSTime,
    },
    tiresTelemetry: {
      lastSync: tiresLSTime,
      tyreFrontLeftTemp: {
        c: {
          ...tyreTempConfig,
          value: tyreFrontLeftTemp,
        },
        f: {
          value: tyreFrontLeftTempValue,
        },
      },
      tyreFrontRightTemp: {
        c: {
          ...tyreTempConfig,
          value: tyreFrontRightTemp,
        },
        f: {
          value: tyreFrontRightTempValue,
        },
      },
      tyreFrontLeftPressure: {
        kPa: {
          ...tyrePressureConfig,
          value: tyreFrontLeftPressure,
        },
        psi: {
          value: tyreFrontLeftPressurePsi,
        },
        bar: {
          value: tyreFrontLeftPressureBar,
        },
      },
      tyreFrontRightPressure: {
        kPa: {
          ...tyrePressureConfig,
          value: tyreFrontRightPressure,
        },
        psi: {
          value: tyreFrontRightPressurePsi,
        },
        bar: {
          value: tyreFrontRightPressureBar,
        },
      },

      tyreRearLeftOutsideTemp: {
        c: {
          ...tyreTempConfig,
          value: tyreRearLeftOutsideTemp,
        },
        f: {
          value: tyreRearLeftOutsideTempValue,
        },
      },
      tyreRearRightOutsideTemp: {
        c: {
          ...tyreTempConfig,
          value: tyreRearRightOutsideTemp,
        },
        f: {
          value: tyreRearRightOutsideTempValue,
        },
      },
      tyreRearLeftOutsidePressure: {
        kPa: {
          ...tyrePressureConfig,
          value: tyreRearLeftOutsidePressure,
        },
        psi: {
          value: tyreRearLeftOutsidePressurePsi,
        },
        bar: {
          value: tyreRearLeftOutsidePressureBar,
        },
      },
      tyreRearRightOutsidePressure: {
        kPa: {
          ...tyrePressureConfig,
          value: tyreRearRightOutsidePressure,
        },
        psi: {
          value: tyreRearRightOutsidePressurePsi,
        },
        bar: {
          value: tyreRearRightOutsidePressureBar,
        },
      },

      tyreRearLeftInsideTemp: {
        c: {
          ...tyreTempConfig,
          value: tyreRearLeftInsideTemp,
        },
        f: {
          value: tyreRearLeftInsideTempValue,
        },
      },
      tyreRearRightInsideTemp: {
        c: {
          ...tyreTempConfig,
          value: tyreRearRightInsideTemp,
        },
        f: {
          value: tyreRearRightInsideTempValue,
        },
      },
      tyreRearLeftInsidePressure: {
        kPa: {
          ...tyrePressureConfig,
          value: tyreRearLeftInsidePressure,
        },
        psi: {
          value: tyreRearLeftInsidePressurePsi,
        },
        bar: {
          value: tyreRearLeftInsidePressureBar,
        },
      },
      tyreRearRightInsidePressure: {
        kPa: {
          ...tyrePressureConfig,
          value: tyreRearRightInsidePressure,
        },
        psi: {
          value: tyreRearRightInsidePressurePsi,
        },
        bar: {
          value: tyreRearRightInsidePressureBar,
        },
      },
    },
    voltage: {
      ...(!voltageValue || voltageValue < 18 ? voltage12Config : voltage24Config),
      value: voltageValue,
      lastSync: voltageLSTime,
    },
  };
}
